import formatDate from "@DateTime/functions/formatDate";
import {useTranslation} from "@Translation/hooks/useTranslation";
import dateIntervalFormatter from "@MyProfile/functions/dateIntervalFormatter";
import VoucherType from "@MyVoucher/types/Voucher.type";
import VoucherRestrictions from "@MyVoucher/features/voucherDetails/components/VoucherRestrictions";
import TrvlMyProfileNotification from "@MyProfile/components/TrvlMyProfileNotification";
import IconSpecialInfo from "@Icons/searchRes/amenities/IconSpecialInfo";
import IconTrvlClock from "@Icons/time/IconTrvlClock";
import {Palette} from "@Styles/Palette.style";

interface VoucherNotificationSectionProps {
    voucher: VoucherType,
}

function VoucherNotificationSection({voucher}: VoucherNotificationSectionProps) {
    const {
        expiresAt,
        createdAt,
        useCountLimit,
        buyerName,
        buyerEmail,
        type,
        cancelledTicket,
        restrictions,
    } = voucher

    const {
        valid_time: validTimeLabel,
        valid_one_time: validOneTimeLabel,
        voucher_not_transferable: voucherNotTransferableLabel,
        voucher_usable_for: voucherUsableForLabel,
        voucher_not_usable_for: voucherNotUsableForLabel,
        trips_operated_by: tripsOperatedByLabel,
        expiration: expirationLabel,
    } = useTranslation('my_profile.my_vouchers')

    const validMoreThanOneTimeLabel = useTranslation(
        'my_profile.my_vouchers.valid_more_than_one_time',
        {countLimit: useCountLimit === undefined ? "unlimited number of" : useCountLimit}
    )

    const validityPeriodLabel = useTranslation(
        'my_profile.my_vouchers.validity_period',
        {
            period: dateIntervalFormatter({
                startDate: createdAt,
                endDate: expiresAt,
            }),
        }
    )

    const voucherOwnerLabel = useTranslation(
        'my_profile.my_vouchers.voucher_owner',
        {buyerName, buyerEmail}
    )

    const numberOfUsagesMessage = () => {
        if (useCountLimit === 1) {
            return validOneTimeLabel
        }
        return validMoreThanOneTimeLabel
    }

    return (
        <>
            {type === "promo" ? (
                <TrvlMyProfileNotification
                    Icon={IconTrvlClock}
                    iconProps={{fill: Palette.tomato}}
                    title={validityPeriodLabel}
                    description={numberOfUsagesMessage()}
                />
            ) : (
                <TrvlMyProfileNotification
                    Icon={IconTrvlClock}
                    iconProps={{fill: Palette.tomato}}
                    title={`${expirationLabel} ${formatDate(expiresAt, "MMMM, dd, yyyy.")}`}
                    description={validTimeLabel}
                />
            )}
            {type === "promo" && (
                <TrvlMyProfileNotification
                    Icon={IconSpecialInfo}
                    iconProps={{stroke: Palette.cornflowerBlue}}
                    title={voucherOwnerLabel}
                    description={voucherNotTransferableLabel}
                />
            )}
            {type === "promo" && Object.keys(restrictions).some((key) => (restrictions[key].length !== 0)) && (
                <TrvlMyProfileNotification
                    Icon={IconSpecialInfo}
                    iconProps={{stroke: Palette.cornflowerBlue}}
                    title={voucherUsableForLabel}
                    description={<VoucherRestrictions limitations={restrictions}/>}
                />
            )}
            {restrictions.unusableForCompanies.length !== 0 && (
                <TrvlMyProfileNotification
                    Icon={IconSpecialInfo}
                    iconProps={{stroke: Palette.cornflowerBlue}}
                    title={voucherNotUsableForLabel}
                    description={`${tripsOperatedByLabel} ${restrictions.unusableForCompanies.join(", ")}`}
                />
            )}
            {type === "leftover" && (
                <TrvlMyProfileNotification
                    Icon={IconSpecialInfo}
                    iconProps={{stroke: Palette.cornflowerBlue}}
                    title={`This voucher was issued after rebook voucher ${cancelledTicket} wasn't used in its full amount.`}
                    description="Any leftover voucher is valid until the expiry date of the rebook voucher it was initially created from."
                />
            )}
        </>
    );
}

export default VoucherNotificationSection